const API_BASE = '/api/v1/chat';

export interface Agent {
  id: string;
  name: string;
  description: string;
  specialization: string;
  icon: string;
  color: string;
}

export interface Message {
  id: number;
  role: 'user' | 'assistant' | 'system';
  content: string;
  agentType?: string;
  createdAt: string;
}

export interface ChatAttachment {
  fileId?: number;
  filename: string;
  type: 'text' | 'image';
  content?: string;
  mimeType?: string;
}

export interface Conversation {
  id: number;
  title: string;
  agentType: string;
  summary?: string;
  archived: boolean;
  pinned: boolean;
  lastMessage?: string;
  updatedAt: string;
  createdAt: string;
  messages?: Message[];
}

async function handleResponse<T>(response: Response): Promise<T> {
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error || 'Errore sconosciuto');
  }
  return data;
}

export async function getAgents(): Promise<{ success: boolean; agents: Agent[] }> {
  const response = await fetch(`${API_BASE}/agents`, {
    credentials: 'include',
  });
  return handleResponse(response);
}

export async function getConversations(): Promise<{
  success: boolean;
  conversations: Conversation[];
}> {
  const response = await fetch(`${API_BASE}/conversations`, {
    credentials: 'include',
  });
  return handleResponse(response);
}

export async function createConversation(
  agentType: string,
  title?: string
): Promise<{ success: boolean; conversation: Conversation }> {
  const response = await fetch(`${API_BASE}/conversations`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ agentType, title }),
  });
  return handleResponse(response);
}

export async function getConversation(
  id: number
): Promise<{ success: boolean; conversation: Conversation }> {
  const response = await fetch(`${API_BASE}/conversations/${id}`, {
    credentials: 'include',
  });
  return handleResponse(response);
}

export interface LLMInfo {
  provider: string;
  model: string;
  usingFallback: boolean;
}

export async function sendMessage(
  conversationId: number,
  content: string,
  attachments?: ChatAttachment[]
): Promise<{
  success: boolean;
  userMessage: Message;
  assistantMessage: Message;
  llmInfo?: LLMInfo;
}> {
  const response = await fetch(
    `${API_BASE}/conversations/${conversationId}/messages`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ content, attachments }),
    }
  );
  return handleResponse(response);
}

export async function deleteConversation(
  id: number
): Promise<{ success: boolean }> {
  const response = await fetch(`${API_BASE}/conversations/${id}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  return handleResponse(response);
}

export async function updateConversation(
  id: number,
  updates: { title?: string; archived?: boolean; pinned?: boolean }
): Promise<{ success: boolean; conversation: Conversation }> {
  const response = await fetch(`${API_BASE}/conversations/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(updates),
  });
  return handleResponse(response);
}

export async function exportConversationPdf(id: number): Promise<Blob> {
  const response = await fetch(`${API_BASE}/conversations/${id}/export/pdf`, {
    credentials: 'include',
  });
  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.error || 'Errore nell\'esportazione PDF');
  }
  return response.blob();
}
